import Image from 'next/image';
import Link from 'next/link';
import type { StaticImageData } from 'next/image';
import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { FormProps } from 'tdesign-react';
import { Button, Dropdown, Form } from 'tdesign-react';
import {
  handleLogout,
  ruleChineseEnglishOnly,
  ruleNoChinese,
  ruleLength,
  ruleRequired,
} from '@ssv-website/hooks';
import { Logo } from '../../logo';
import { getUploadPreviewUrl } from './AvatarUploadField';
import {
  BIO_EN_PLACEHOLDER,
  BIO_ZH_PLACEHOLDER,
  HERO_SUBTITLE,
  HERO_TITLE,
  MEETING_DATE_TEXT,
  MEETING_DESCRIPTION,
  MEETING_LOCATION_TEXT,
  MEETING_TITLE,
  MOBILE_INPUT_PLACEHOLDER,
  MOBILE_TEXTAREA_SECTION_CLASS,
} from './config';
import {
  ContactPermissionField,
  MobileAvatarField,
  MobileInputField,
  MobileTextareaField,
  TitleListFields,
  emailRules,
  phoneRules,
} from './form-fields';
import { CalendarIcon, LocationIcon, UserCircleIcon } from './icons';
import { MeetingInfo } from './shared';
import type { SignupFormValues } from './types';
import { getLanguageSwitchMeta, switchLanguage } from '../i18n/config';
import { useI18nContext } from '../i18n/I18nProvider';

const SUBMITTED_EMPTY_TEXT = '-';

const getSubmittedText = (value: unknown) => {
  const text = String(value ?? '').trim();
  return text || SUBMITTED_EMPTY_TEXT;
};

function MobileSubmittedStatusIcon() {
  return (
    <span className="inline-flex h-14 w-14 items-center justify-center text-[#2BA471]">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="56"
        height="56"
        viewBox="0 0 56 56"
        fill="none"
      >
        <path
          d="M15.75 28.7227L24.5 37.4745L40.25 21.7262L37.7738 19.25L24.5 32.5255L18.2227 26.25L15.75 28.7227Z"
          fill="#2BA471"
        />
        <path
          d="M14.3885 7.62899C18.4175 4.9369 23.1544 3.5 28 3.5C34.4978 3.5 40.7295 6.08124 45.3241 10.6759C49.9188 15.2705 52.5 21.5022 52.5 28C52.5 32.8456 51.0631 37.5825 48.371 41.6115C45.6789 45.6405 41.8525 48.7807 37.3758 50.635C32.899 52.4894 27.9728 52.9746 23.2203 52.0292C18.4678 51.0839 14.1023 48.7505 10.6759 45.3241C7.24951 41.8977 4.91611 37.5322 3.97077 32.7797C3.02543 28.0272 3.51062 23.101 5.36496 18.6243C7.21931 14.1475 10.3595 10.3211 14.3885 7.62899ZM16.333 45.4609C19.7865 47.7684 23.8466 49 28 49C33.5696 49 38.911 46.7875 42.8493 42.8492C46.7875 38.911 49 33.5695 49 28C49 23.8466 47.7684 19.7865 45.4609 16.333C43.1534 12.8796 39.8736 10.188 36.0364 8.59853C32.1991 7.00909 27.9767 6.59322 23.9031 7.40351C19.8295 8.2138 16.0877 10.2139 13.1508 13.1508C10.2139 16.0877 8.21381 19.8295 7.40352 23.9031C6.59323 27.9767 7.0091 32.1991 8.59854 36.0364C10.188 39.8736 12.8796 43.1534 16.333 45.4609Z"
          fill="#2BA471"
        />
      </svg>
    </span>
  );
}

function MobileSubmitLoading({ text }: { text: string }) {
  return (
    <span className="inline-flex items-center justify-center gap-2">
      <span
        aria-hidden="true"
        className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
      />
      <span>{text}</span>
    </span>
  );
}

function MobileSubmittedCell({
  label,
  children,
  showDivider = true,
}: {
  label: string;
  children: ReactNode;
  showDivider?: boolean;
}) {
  return (
    <div
      className={`px-6 py-5 ${showDivider ? 'border-b border-[#E7E7E7]' : ''}`}
    >
      <div className="text-sm leading-[22px] text-[rgba(0,0,0,0.4)]">
        {label}
      </div>
      {/* 添加 break-words 以兼容纯英文长单词换行 */}
      <div className="mt-1 text-base leading-6 text-[rgba(0,0,0,0.9)] break-words">
        {children}
      </div>
    </div>
  );
}

function MobileSubmittedDetail({ values }: { values: SignupFormValues }) {
  const avatarUrl = getUploadPreviewUrl(values.avatar);
  const titleItems = values.titles.filter(
    (item) => item.titleCn.trim() || item.titleEn.trim()
  );
  const detailItems = [
    {
      label: '中文姓名',
      value: getSubmittedText(values.nameCn),
    },
    {
      label: '英文姓名',
      value: getSubmittedText(values.nameEn),
    },
    ...titleItems.flatMap((item, index) => [
      {
        label: `中文头衔${index + 1}`,
        value: getSubmittedText(item.titleCn),
      },
      {
        label: `英文头衔${index + 1}`,
        value: getSubmittedText(item.titleEn),
      },
    ]),
    // {
    //   label: '国籍',
    //   value: getSubmittedText(values.nationality),
    // },
    {
      label: '电话',
      value: getSubmittedText(values.phone),
    },
    {
      label: '邮箱',
      value: getSubmittedText(values.email),
    },
  ];

  return (
    <div className="">
      <div className="px-6 pb-5">
        <div className="text-sm leading-[22px] text-[rgba(0,0,0,0.4)]">
          头像
        </div>
        <div className="mt-1 h-[105px] w-[105px] overflow-hidden rounded-[8px] bg-[#F2F3F4]">
          {avatarUrl ? (
            <div
              className="h-full w-full bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${avatarUrl})` }}
            />
          ) : null}
        </div>
      </div>
      <div className="mx-6 h-px bg-[#E7E7E7]" />

      {detailItems.map((item) => (
        <MobileSubmittedCell
          key={`${item.label}-${item.value}`}
          label={item.label}
        >
          {item.value}
        </MobileSubmittedCell>
      ))}

      <div className="px-6 pt-5">
        <div className="text-sm leading-[22px] text-[rgba(0,0,0,0.4)]">
          个人简介
        </div>
        <div className="mt-1 space-y-2 pb-5 text-sm leading-5 text-[rgba(0,0,0,0.9)] break-words">
          <p>{getSubmittedText(values.bioCn)}</p>
          <p>{getSubmittedText(values.bioEn)}</p>
        </div>
      </div>
      <div className="mx-6 h-px bg-[#E7E7E7]" />

      <MobileSubmittedCell
        label="是否允许通过站内信联系您(您可以收取站内通知、好友申请、联系人留言等信息)"
        showDivider={false}
      >
        {values.allowContact === 0 ? '拒绝' : '允许'}
      </MobileSubmittedCell>
    </div>
  );
}

type MobileSignupPageProps = {
  displayedPhone: string;
  form: any;
  heroBackgroundSrc: string;
  heroIllustration?: StaticImageData;
  heroIllustrationAlt: string;
  isEnteringFromEdit: boolean;
  isLogin: boolean;
  isReadonlySubmittedState: boolean;
  isSubmitDisabled: boolean;
  isSubmitting: boolean;
  isSubmitSuccessState?: boolean; // 是否是从编辑/新增成功跳转过来的状态
  onPrimaryAction: () => void;
  onSubmit: FormProps['onSubmit'];
  readonlyValues: SignupFormValues;
  submitButtonText: string;
  submitSuccessTitle?: string;
  syncSubmitState: (...args: any[]) => void;
};

function LoginSuccessNotice({ onClose }: { onClose: () => void }) {
  return createPortal(
    <div className="fixed left-0 right-0 top-[56px] z-[100] px-4 py-3">
      <div className="flex items-center gap-3 rounded-lg bg-[#fff] px-4 py-3 shadow-sm">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39762 14.6024 1.66667 10 1.66667C5.39762 1.66667 1.66667 5.39762 1.66667 10C1.66667 14.6024 5.39762 18.3333 10 18.3333Z"
            fill="#2BA471"
          />
          <path
            d="M6.45801 10.625L8.81634 12.7083L13.5413 7.29167"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="flex-1 text-sm leading-[22px] text-[rgba(0,0,0,0.9)]">
          登录成功，请尽快补充参会信息并提交
        </span>
        <button
          type="button"
          onClick={onClose}
          className="flex h-6 w-6 items-center justify-center text-[rgba(0,0,0,0.4)]"
          aria-label="关闭"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M4 4L12 12M4 12L12 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>,
    document.body
  );
}

export function MobileSignupPage({
  displayedPhone,
  form,
  heroBackgroundSrc,
  heroIllustration,
  heroIllustrationAlt,
  isEnteringFromEdit,
  isLogin,
  isReadonlySubmittedState,
  isSubmitDisabled,
  isSubmitting,
  isSubmitSuccessState = false,
  onPrimaryAction,
  onSubmit,
  readonlyValues,
  submitButtonText,
  submitSuccessTitle = '提交成功',
  syncSubmitState,
}: MobileSignupPageProps) {
  const { changeLocale } = useI18nContext();
  const langMeta = getLanguageSwitchMeta('zh'); // CN page, toggle target is EN
  const [hasScrolled, setHasScrolled] = useState(false);
  // 登录成功提示显示条件：已登录、未提交过信息、不是从编辑页进来
  const [showLoginNotice, setShowLoginNotice] = useState(
    !isEnteringFromEdit && !isReadonlySubmittedState
  );

  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 60);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const userDropdownOptions = [
    {
      content: (
        <div className="text-base leading-[1.8] text-center">退出登录</div>
      ),
      value: 1,
    },
  ];

  const handleDropdownClick: import('tdesign-react').DropdownProps['onClick'] =
    (dropdownItem) => {
      if (dropdownItem.value === 1) {
        handleLogout();
      }
    };

  return (
    <div className="signup-2026-mobile min-h-screen overflow-hidden bg-white text-[#121212]">
      <div className="relative min-h-screen w-full bg-white pb-[calc(env(safe-area-inset-bottom)+64px)]">
        {/* Header with Logo and User */}
        <header
          className={`fixed left-0 right-0 top-0 z-20 flex items-center justify-between px-5 py-4 transition-colors duration-200 ${
            hasScrolled ? 'bg-white' : 'bg-transparent'
          }`}
        >
          <Link href="/" className="inline-flex items-center">
            <Logo type="white" className="h-7 w-[72px] object-contain" />
          </Link>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                changeLocale(langMeta.targetLang);
                switchLanguage(langMeta.targetLang);
              }}
              className="inline-flex items-center gap-1 rounded-full border border-[#000] px-3 py-1.5 text-sm leading-5 text-[#000] hover:bg-[rgba(0,0,0,0.04)]"
              aria-label="Switch to English"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                className="shrink-0"
              >
                <path
                  d="M8 14.6667C11.6819 14.6667 14.6667 11.6819 14.6667 8C14.6667 4.3181 11.6819 1.33337 8 1.33337C4.3181 1.33337 1.33337 4.3181 1.33337 8C1.33337 11.6819 4.3181 14.6667 8 14.6667Z"
                  stroke="currentColor"
                  strokeWidth="1.2"
                />
                <path
                  d="M1.33337 8H14.6667"
                  stroke="currentColor"
                  strokeWidth="1.2"
                />
                <path
                  d="M8 1.33337C6.5 3 5.66667 5.33333 5.66667 8C5.66667 10.6667 6.5 13 8 14.6667C9.5 13 10.3333 10.6667 10.3333 8C10.3333 5.33333 9.5 3 8 1.33337Z"
                  stroke="currentColor"
                  strokeWidth="1.2"
                />
              </svg>
              {langMeta.langLabel}
            </button>
            <Dropdown
              hideAfterItemClick
              onClick={handleDropdownClick}
              options={userDropdownOptions}
              placement="bottom-right"
              trigger="click"
            >
              <div className="inline-flex items-center gap-1 text-[rgba(0,0,0,0.9)]">
                <UserCircleIcon />
                <span className="max-w-[100px] truncate text-sm leading-[22px]">
                  {displayedPhone || '--'}
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="text-[rgba(0,0,0,0.9)]"
                >
                  <path
                    d="M3.5 6L8 10.5L12.5 6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </Dropdown>
          </div>
        </header>

        {/* Login Success Notice */}
        {isLogin && !isReadonlySubmittedState && showLoginNotice && (
          <LoginSuccessNotice onClose={() => setShowLoginNotice(false)} />
        )}

        <section className="relative pt-[64px]">
          <div
            className="absolute inset-x-0 top-0 h-[582px] bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${heroBackgroundSrc})` }}
          />
          {heroIllustration ? (
            <Image
              src={heroIllustration}
              alt={heroIllustrationAlt}
              priority
              sizes="228px"
              className="absolute right-[-16px] top-[-36px] h-[250px] w-[228px] object-contain"
            />
          ) : null}

          <div className="relative z-10 px-5 py-8">
            <div className="w-[244px]">
              <p className="text-[32px] font-semibold leading-[40px] text-[#0052D9]">
                {HERO_TITLE}
              </p>
              <p className="mt-[2px] text-sm font-medium leading-[22px] text-[#0052D9]">
                {HERO_SUBTITLE}
              </p>
            </div>

            <div className="mt-4 flex flex-col gap-1 text-xs leading-[18px] text-[#000000E5]">
              <MeetingInfo icon={<CalendarIcon small />}>
                {MEETING_DATE_TEXT}
              </MeetingInfo>
              <MeetingInfo icon={<LocationIcon small />}>
                {MEETING_LOCATION_TEXT}
              </MeetingInfo>
            </div>
          </div>
        </section>

        <section
          className={`relative z-10 overflow-hidden rounded-t-[20px] ${
            isReadonlySubmittedState
              ? 'border border-white bg-[rgba(255,255,255,0.50)] backdrop-blur-[8px]'
              : 'bg-[rgba(255,255,255,0.60)]'
          }`}
        >
          <div className="absolute inset-x-0 top-[582px] bottom-0 bg-white" />

          {isReadonlySubmittedState ? (
            <>
              {isSubmitSuccessState && (
                <div className="relative z-10 flex flex-col items-center gap-3 pb-[25px] pt-[23px]">
                  <MobileSubmittedStatusIcon />
                  <h1 className="text-[18px] font-semibold leading-[26px] text-[rgba(0,0,0,0.9)]">
                    {submitSuccessTitle}
                  </h1>
                </div>
              )}

              <div className="relative z-10 flex h-[54px] items-center ">
                <span className="h-4 w-1 rounded-r-[2px] bg-[#5590F6]" />
                <span className="ml-4 text-base font-medium leading-[22px] text-[rgba(0,0,0,0.9)]">
                  基础信息
                </span>
              </div>

              <div className="relative z-10">
                <MobileSubmittedDetail values={readonlyValues} />
              </div>
            </>
          ) : (
            <>
              <div className="px-5 py-6">
                <h1 className="text-[18px] font-medium leading-[26px] text-[rgba(0,0,0,0.9)]">
                  {MEETING_TITLE}
                </h1>
                <p className="mt-2 text-sm leading-[22px] text-[rgba(0,0,0,0.4)]">
                  {MEETING_DESCRIPTION}
                </p>
              </div>

              <div className="flex h-[54px] items-center">
                <span className="h-4 w-1 rounded-r-[2px] bg-[#0052D9]" />
                <span className="ml-4 text-base font-medium leading-[22px] text-[rgba(0,0,0,0.9)]">
                  基础信息
                </span>
              </div>

              <Form
                form={form}
                initialData={readonlyValues}
                labelAlign="top"
                layout="vertical"
                preventSubmitDefault
                requiredMark={false}
                scrollToFirstError="smooth"
                showErrorMessage
                statusIcon={false}
                submitWithWarningMessage={false}
                onSubmit={onSubmit}
                onValuesChange={syncSubmitState}
              >
                <div className="signup-2026-mobile-section px-6 pb-5 pt-5">
                  <MobileAvatarField onUploadStateChange={syncSubmitState} />
                </div>

                <MobileInputField
                  label="中文姓名"
                  name="nameCn"
                  placeholder="请输入中文姓名"
                  required
                  rules={[
                    ruleRequired({ text: '中文姓名' }),
                    ruleChineseEnglishOnly,
                    ruleLength(20),
                  ]}
                  maxlength={20}
                />
                <MobileInputField
                  label="英文姓名"
                  name="nameEn"
                  placeholder="请输入英文姓名"
                  required
                  rules={[
                    ruleRequired({ text: '英文姓名' }),
                    ruleNoChinese,
                    ruleLength(50),
                  ]}
                  maxlength={50}
                />

                <TitleListFields form={form} mobile />

                {/* <MobileInputField
                  label="国籍"
                  name="nationality"
                  placeholder={MOBILE_INPUT_PLACEHOLDER}
                  required
                  rules={[ruleRequired({ text: '国籍' })]}
                /> */}
                <MobileInputField
                  label="电话"
                  name="phone"
                  placeholder={MOBILE_INPUT_PLACEHOLDER}
                  required
                  rules={phoneRules}
                  maxlength={20}
                  readonly
                />
                <MobileInputField
                  label="邮箱"
                  name="email"
                  placeholder="请输入邮箱地址"
                  required
                  rules={emailRules}
                  maxlength={100}
                />

                <div className={MOBILE_TEXTAREA_SECTION_CLASS}>
                  <div className="flex flex-col gap-5 pb-5">
                    <MobileTextareaField
                      label="个人简介"
                      name="bioCn"
                      placeholder={BIO_ZH_PLACEHOLDER}
                      required
                      rules={[
                        ruleRequired({ text: '个人简介' }),
                        ruleLength(600),
                      ]}
                      maxlength={600}
                    />
                    <div className="mt-[-16px]">
                      <MobileTextareaField
                        label="个人简介（英文）"
                        required
                        name="bioEn"
                        placeholder={BIO_EN_PLACEHOLDER}
                        rules={[
                          ruleRequired({ text: '个人简介（英文）' }),
                          ruleNoChinese,
                          ruleLength(600),
                        ]}
                        maxlength={600}
                      />
                    </div>
                  </div>
                </div>

                <ContactPermissionField />
              </Form>
            </>
          )}
        </section>

        <div className="fixed inset-x-0 bottom-0 z-30 w-full bg-white px-4 pb-[calc(env(safe-area-inset-bottom)+8px)] pt-2 shadow-[0_-6px_12px_rgba(0,0,0,0.06)]">
          <Button
            block
            size="large"
            disabled={isSubmitDisabled || isSubmitting}
            className={`signup-2026-mobile-submit-button !h-12 !rounded !border-0 !text-base !font-normal !leading-6 ${
              isSubmitDisabled || isSubmitting
                ? '!bg-[#B5C7FF] !text-white'
                : '!bg-[#0052D9] !text-white'
            }`}
            onClick={onPrimaryAction}
          >
            {isSubmitting ? (
              <MobileSubmitLoading text={submitButtonText} />
            ) : (
              submitButtonText
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
